import {FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo} from "react";
import {FeatureCollection, Polygon} from "geojson";
import * as turf from "@turf/turf";
import {MapboxCoordinates} from "../index";
import MapboxMarkers, {MapboxMarker} from "../markers";
import {GeoJSONSource, Map} from "mapbox-gl";


export interface MapboxCircle {
    id: string;
    center: MapboxCoordinates,
    radiusInMeters: number,
    fillColor: string,
    fillOutlineColor: string,
    fillOpacity?: number,
    markerIcon?: boolean,
    markerColor?: string,
    flyToMarkerOnClick?: boolean,
}

export interface MapCircleProps {
    map?: Map,
    circles?: MapboxCircle[],
    sourceId?: string,
    layerId?: string,
    markerColor?: string;
    flyToMarkerOnClick?: boolean;
}

const MapboxCircles: FunctionComponent<MapCircleProps> = (props) => {
    const sourceId = useMemo(() => (props.sourceId ?? `circles-source`), [props.sourceId]);
    const layerId = useMemo(() => (props.layerId ?? `circles-layer`), [props.layerId]);

    const markers = useMemo<MapboxMarker[]>(() =>
            props.circles
                ?.filter(circle => !!circle.markerIcon)
                ?.map(circle => ({
                    id: circle.id,
                    center: circle.center,
                    color: circle.markerColor ?? props.markerColor,
                    flyToOnClick: circle.flyToMarkerOnClick ?? props.flyToMarkerOnClick,
                }))
            ?? [],
        [props.circles, props.flyToMarkerOnClick, props.markerColor]);

    /**
     * Creates circles on the map using the circles prop.
     */
    const createCircles = useCallback((map: Map) => {
        // Create GeoJSON features from circles using Turf.
        const data: FeatureCollection<Polygon> = {
            type: 'FeatureCollection',
            features: props.circles!.map((circle) => {
                const center = [circle.center.lng, circle.center.lat];
                const polygon = turf.circle(center, circle.radiusInMeters / 1000, {
                    steps: 256,
                    units: 'kilometers', // Turf expects radius in kilometers.
                });
                return {
                    type: 'Feature',
                    geometry: polygon.geometry,
                    properties: {
                        fillColor: circle.fillColor,
                        fillOutlineColor: circle.fillOutlineColor,
                        fillOpacity: circle.fillOpacity ?? 0.5,
                    }
                };
            }),
        };

        const source = map.getSource(sourceId);
        if (!!source) {
            (source as GeoJSONSource).setData(data);
        } else {
            map.addSource(sourceId, {type: 'geojson', data});
            const layer = map.getLayer(layerId);
            if (!layer) {
                map.addLayer({
                    id: layerId,
                    type: 'fill',
                    source: sourceId,
                    layout: {},
                    paint: {
                        'fill-color': ['get', 'fillColor'],
                        'fill-outline-color': ['get', 'fillOutlineColor'],
                        'fill-opacity': ['get', 'fillOpacity'],
                    }
                });
            } else {
                layer.source = sourceId;
            }
        }
    }, [layerId, props.circles, sourceId]);

    /**
     * When the map or circles change, update the map:
     * - Create circles on the map.
     */
    useLayoutEffect(() => {
        if (!props.map || !props.circles?.length)
            return;
        const map = props.map;
        const circlesGenerator = () => createCircles(map);
        if (map.isStyleLoaded()) circlesGenerator();
        else map.once('style.load', circlesGenerator);
        return () => {
            map.off('style.load', circlesGenerator);
        };
    }, [createCircles, props.map, props.circles, layerId, sourceId]);

    /**
     * Removes the source and layer when the component is unmounted.
     */
    useEffect(() => () => {
        if (props.map?.getLayer(layerId)) {
            props.map?.removeLayer(layerId);
        }
        if (props.map?.getSource(sourceId)) {
            props.map?.removeSource(sourceId);
        }
    }, [layerId, props.map, sourceId]);

    return (
        <>
            <MapboxMarkers
                map={props.map}
                markers={markers}
                markerColor={props.markerColor}
                flyToMarkerOnClick={props.flyToMarkerOnClick}
            />
        </>
    )
};

export default MapboxCircles;